import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { updateUserPreferences } from "../api";
import type { ThemePreference } from "../types";

type ResolvedTheme = "light" | "dark";

interface ThemeContextType {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === "system") {
    return getSystemTheme();
  }
  return preference;
}

function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
}

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: ThemePreference;
}

export function ThemeProvider({ children, initialTheme = "system" }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemePreference>(initialTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(initialTheme)
  );
  const [isLoading, setIsLoading] = useState(false);

  // Apply theme immediately when resolved theme changes
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Listen for system theme changes when in system mode
  useEffect(() => {
    if (theme !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const newResolved = getSystemTheme();
      setResolvedTheme(newResolved);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  // Update theme state when initialTheme changes (user logs in)
  useEffect(() => {
    setThemeState(initialTheme);
    setResolvedTheme(resolveTheme(initialTheme));
  }, [initialTheme]);

  const setTheme = useCallback(
    async (newTheme: ThemePreference) => {
      const previousTheme = theme;
      setIsLoading(true);

      try {
        // Optimistically update UI
        setThemeState(newTheme);
        setResolvedTheme(resolveTheme(newTheme));

        // Cache in localStorage for flash prevention
        localStorage.setItem("theme-preference", newTheme);

        // Persist to backend
        await updateUserPreferences({ theme: newTheme });
      } catch {
        // Revert on error
        setThemeState(previousTheme);
        setResolvedTheme(resolveTheme(previousTheme));
        localStorage.setItem("theme-preference", previousTheme);
        throw new Error("Failed to save theme preference");
      } finally {
        setIsLoading(false);
      }
    },
    [theme]
  );

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}
